import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SettingsHostProps, SettingsItem } from 'shared/settings';
import { usePermissions } from '@/providers/Permissions.provider';
import isBrandingLocked from './isBrandingLocked.helper';

// The one place partner-dashboard maps its own permission model onto the shared Settings shell's
// props — the partner counterpart of client-dashboard's useSettingsHostProps (ID-13819), with the
// Partner Portal's two cards (ID-13820). Card copy mirrors the Account Portal's so both portals read
// the same. `children` (the inner-route <Switch>) is passed by SettingsPage alongside these props.
export default function useSettingsHostProps(): Omit<SettingsHostProps, 'children'> {
  const history = useHistory();
  const { permissions } = usePermissions();

  const { t } = useTranslation('translations', { keyPrefix: 'Settings' });
  const { t: tCards } = useTranslation('translations', { keyPrefix: 'Settings.Cards' });

  const items: SettingsItem[] = [
    {
      id: 'branding',
      icon: 'brandSettings',
      path: '/settings/brand-settings',
      title: tCards('Branding.title', { defaultValue: 'Branding Settings' }),
      subtitle: tCards('Branding.subtitle', { defaultValue: 'Show your brand to your customers' }),
      description: tCards('Branding.description', {
        defaultValue: 'Upload your logo and set your colours across the emails, forms and pages your customers see',
      }),
      buttonLabel: tCards('Branding.button', { defaultValue: 'Manage Branding' }),
      isLocked: isBrandingLocked(permissions),
      lockedDescription: tCards('Branding.lockedDescription', {
        defaultValue: 'Upload your logo and set your colours across the emails, forms and pages your customers see.',
      }),
    },
    {
      id: 'webhooks',
      icon: 'webhooks',
      path: '/settings/webhooks',
      title: tCards('Webhooks.title', { defaultValue: 'Webhooks' }),
      subtitle: tCards('Webhooks.subtitle', { defaultValue: 'Get real-time event notifications' }),
      description: tCards('Webhooks.description', {
        defaultValue: 'Add your endpoint URLs and choose which transaction and account events to receive',
      }),
      buttonLabel: tCards('Webhooks.button', { defaultValue: 'Manage Webhooks' }),
    },
  ];

  return {
    title: t('pageTitle', { defaultValue: 'Settings & Configuration' }),
    basePath: '/settings',
    items,
    hostActions: {
      onRequestAccess: (item) =>
        history.push({
          pathname: '/tickets-create',
          state: { ticketSubject: `Enquiry about ${item.title} feature` },
        }),
    },
  };
}
